import { authenticatedInstance } from '../index/index.service';
import type {
  ActiveSessionView as SharedActiveSessionView,
  AttendanceReportRules,
  RecordCashMoveRequest,
  SessionFilterOption,
  SessionSalesSummaryView,
  SessionView,
  ZReportPrintRequest,
  UpdateZReportEmailRecipientsRequest,
  SendZReportEmailRequest,
} from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type ActiveSessionView = SharedActiveSessionView & {
  openDrawerOnPartialCash?: boolean;
  openDrawerEmployeeIds?: number[];
  openDrawerRequiresPassword?: boolean;
  openDrawerPasswordConfigured?: boolean;
  employeeAttendanceReportEnabled?: boolean;
  attendanceReportRules?: AttendanceReportRules;
};
export type { SessionFilterOption, SessionSalesSummaryView, SessionState, SessionView } from 'shared-models/browser';

// Raised when close is rejected because orders opened mid-flow; count is null when unparsed.
export class SessionHasOpenOrdersError extends Error {
  constructor(public readonly openOrderCount: number | null) {
    super('SESSION_HAS_OPEN_ORDERS');
    this.name = 'SessionHasOpenOrdersError';
  }
}

// Result of a manager close attempt, driving the caller's print-vs-guard decision.
export type SessionCloseOutcome =
  | { status: 'closed' }
  | { status: 'open-orders'; openOrderCount: number | null }
  | { status: 'error' };

class SessionsService {
  // Summary: Loads the active session + auto-close time so the shell gates and renders the tile.
  // Details: docs/function-library/web/services.md#sessions-service
  async getActive(tenantId: string, employeeSession: string): Promise<ActiveSessionView> {
    try {
      const response = await authenticatedInstance.get<ActiveSessionView>(`/sessions/tenant/${tenantId}/active`, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Loads closed-order count + sales total for the active session, powering the close-session ("Z סוף יום") header.
  async getSummary(tenantId: string, employeeSession: string): Promise<SessionSalesSummaryView> {
    try {
      const response = await authenticatedInstance.get<SessionSalesSummaryView>(
        `/sessions/tenant/${tenantId}/summary`,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Opens a tenant session from POS flow; declares opening cash float when provided.
  // Details: docs/function-library/web/services.md#sessions-service
  async open(tenantId: string, employeeSession: string, openingCash?: number): Promise<SessionView> {
    try {
      const body = openingCash != null ? { openingCash } : {};
      const response = await authenticatedInstance.post<SessionView>(
        `/sessions/tenant/${tenantId}/open`,
        body,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Manager corrects the open session's opening cash. Void — SSE echoes the update.
  // Details: docs/function-library/web/services.md#sessions-service
  async updateOpeningCash(tenantId: string, employeeSession: string, openingCash: number): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/opening-cash`,
        { openingCash },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating opening cash'));
    }
  }

  // Summary: Manager records a manual cash in/out move on the open session. Void — 204, no SSE.
  async recordCashMove(tenantId: string, employeeSession: string, payload: RecordCashMoveRequest): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/sessions/tenant/${tenantId}/cash-alterations`,
        payload,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed recording cash move'));
    }
  }

  // Summary: Manager retro-corrects counted cash on a selected closed session.
  async updateClosedCountedCashForSession(
    tenantId: string,
    employeeSession: string,
    sessionId: number,
    closingCash: number,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/latest-closed-counted-cash`,
        { sessionId, closingCash },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating counted cash'));
    }
  }

  // Summary: Manager-only session options for tenant-scoped shift selection workflows.
  async listSessionFilterOptions(tenantId: string, employeeSession: string): Promise<SessionFilterOption[]> {
    try {
      const response = await authenticatedInstance.get<SessionFilterOption[]>(
        `/sessions/tenant/${encodeURIComponent(tenantId)}/filter-options`,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading session options'));
    }
  }

  // Summary: Manager sets the open session's daily brief. Returns void — update arrives via SSE.
  // Details: docs/function-library/web/services.md#sessions-service
  async updateDailyBrief(tenantId: string, text: string, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/daily-brief`,
        { text },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating daily brief'));
    }
  }

  // Summary: Manager set/clear (null = off) the tenant session auto-close time. Void — rides SSE.
  // Details: docs/function-library/web/services.md#sessions-service
  async updateAutoCloseTime(tenantId: string, autoCloseTime: string | null, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/auto-close-time`,
        { autoCloseTime },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating auto-close time'));
    }
  }

  // Summary: Manager sets the tenant-wide POS floor-plan canvas background. Void — rides SSE to every device.
  async updateFloorBackground(
    tenantId: string,
    floorBackground: 'white' | 'image' | 'primary',
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/floor-background`,
        { floorBackground },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating floor background'));
    }
  }

  // Summary: Manager toggles whether employee general-item actions require manager approval.
  async updateGeneralItemApprovalRequired(
    tenantId: string,
    generalItemRequiresManagerApproval: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/general-item-approval`,
        { generalItemRequiresManagerApproval },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating general item approval setting'));
    }
  }

  async updateActionApprovalRequired(
    tenantId: string,
    actionType: 'cancel' | 'global_discount' | 'item_discount' | 'on_the_house',
    required: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/approval-requirements/${actionType}`,
        { required },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating manager approval setting'));
    }
  }
    async updateOperationApprovalRequired(
      tenantId: string,
      actionType: 'transfer' | 'merge' | 'split_merged_table',
      required: boolean,
      employeeSession: string,
    ): Promise<void> {
      try {
        await authenticatedInstance.patch<void>(
          `/sessions/tenant/${tenantId}/operation-approval-requirements/${actionType}`,
          { required },
          { headers: { 'x-employee-session': employeeSession } },
        );
      } catch (error: unknown) {
        throw new Error(extractServiceErrorMessage(error, 'Failed updating manager approval setting'));
      }
    }

  // Summary: Super-admin manager toggles refund 2FA code delivery (email default vs PWA push).
  async updateRefundCodeViaPush(
    tenantId: string,
    refundCodeViaPush: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/refund-code-via-push`,
        { refundCodeViaPush },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating refund code channel setting'));
    }
  }

  async updateFixedRefundCode(tenantId: string, code: string, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/sessions/tenant/${tenantId}/refund-fixed-code`, { code },
        { headers: { 'x-employee-session': employeeSession } });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating fixed refund code'));
    }
  }

  // Summary: Manager toggles orders-page waiter-only list visibility for non-managers.
  async updateOrdersPageWaiterOwnVisibility(
    tenantId: string,
    ordersPageWaiterOwnVisibility: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/orders-page-waiter-own-visibility`,
        { ordersPageWaiterOwnVisibility },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating orders page visibility setting'));
    }
  }

  // Summary: Manager toggles printing the kitchen-bon table number at the bottom of the bon.
  async updateKitchenBonTableNumberAtBottom(
    tenantId: string,
    kitchenBonTableNumberAtBottom: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/kitchen-bon-table-number-at-bottom`,
        { kitchenBonTableNumberAtBottom },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating kitchen bon layout setting'));
    }
  }

  async updateStayInOrderAfterFire(
    tenantId: string,
    stayInOrderAfterFire: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/stay-in-order-after-fire`,
        { stayInOrderAfterFire },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating post-fire navigation setting'));
    }
  }

  async updateEmployeeIdleLogoutMinutes(
    tenantId: string,
    employeeIdleLogoutMinutes: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/employee-idle-logout-minutes`,
        { employeeIdleLogoutMinutes },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating idle logout setting'));
    }
  }

  async updateWarnNoBillBeforePayment(
    tenantId: string,
    warnNoBillBeforePayment: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/warn-no-bill-before-payment`,
        { warnNoBillBeforePayment },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating no-bill warning setting'));
    }
  }

  async updateHideGeneralItemInMenu(
    tenantId: string,
    hideGeneralItemInMenu: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/hide-general-item-in-menu`,
        { hideGeneralItemInMenu },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating general item menu visibility'));
    }
  }

  async updateDisableCashTips(
    tenantId: string,
    disableCashTips: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/disable-cash-tips`,
        { disableCashTips },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating cash tips setting'));
    }
  }

  // Summary: Manager toggles whether non-managers can enter only their own opened orders.
  async updateWaiterOwnOrderEntryOnly(
    tenantId: string,
    waiterOwnOrderEntryOnly: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/waiter-own-order-entry-only`,
        { waiterOwnOrderEntryOnly },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating waiter entry ownership setting'));
    }
  }

  async updateSingleWorkerPerTable(
    tenantId: string,
    singleWorkerPerTable: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/single-worker-per-table`,
        { singleWorkerPerTable },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating single-worker table setting'));
    }
  }

  // Summary: Manager toggles manual-credit card-entry availability in POS payments.
  async updateManualCreditEntryBlocked(
    tenantId: string,
    manualCreditEntryBlocked: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/manual-credit-entry-blocked`,
        { manualCreditEntryBlocked },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating manual credit setting'));
    }
  }

  // Summary: Manager toggles customer-name prompt when opening new dine-in/take-away orders.
  async updateCustomerNameOnOrderEntry(
    tenantId: string,
    customerNameOnOrderEntry: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/customer-name-on-order-entry`,
        { customerNameOnOrderEntry },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating customer name prompt setting'));
    }
  }

  async updateLargeTableNumberOnReceipt(
    tenantId: string,
    largeTableNumberOnReceipt: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/large-table-number-on-receipt`,
        { largeTableNumberOnReceipt },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating large table number receipt setting'));
    }
  }

  // Summary: Manager toggles בחירת מלצר בעמדה נייחת (per-entry waiter picker attribution).
  async updateStationWaiterSelectionEnabled(
    tenantId: string,
    stationWaiterSelectionEnabled: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/station-waiter-selection-enabled`,
        { stationWaiterSelectionEnabled },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating station waiter selection setting'));
    }
  }

  // Summary: Manager sets/clears the tenant stationary station name used for order-open attribution.
  async updateStationaryName(
    tenantId: string,
    stationaryName: string | null,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/stationary-name`,
        { stationaryName },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating stationary name setting'));
    }
  }

  // Summary: Manager toggles whether only managers may close orders with cash.
  async updateCashCloseManagerOnly(
    tenantId: string,
    cashCloseManagerOnly: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/cash-close-manager-only`,
        { cashCloseManagerOnly },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating cash close setting'));
    }
  }

  async updateExcludeOpeningCashFromDrawer(tenantId: string, enabled: boolean, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/exclude-opening-cash-from-drawer`,
        { excludeOpeningCashFromDrawer: enabled },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating report cash setting'));
    }
  }

  async updateRoundAmounts(tenantId: string, roundAmounts: boolean, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/sessions/tenant/${tenantId}/round-amounts`, { roundAmounts },
        { headers: { 'x-employee-session': employeeSession } });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating money rounding setting'));
    }
  }

  // Summary: Manager toggles whether partial cash payments open the cash drawer immediately.
  async updateOpenDrawerOnPartialCash(
    tenantId: string,
    openDrawerOnPartialCash: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/open-drawer-on-partial-cash`,
        { openDrawerOnPartialCash },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating partial cash drawer setting'));
    }
  }

  // Summary: Manager selects non-manager employees allowed to open the drawer manually from POS.
  async updateOpenDrawerEmployeeIds(
    tenantId: string,
    openDrawerEmployeeIds: number[],
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/open-drawer-employee-ids`,
        { openDrawerEmployeeIds },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating drawer employees setting'));
    }
  }

  // Summary: Manager toggles whether manual drawer open requires password confirmation.
  async updateOpenDrawerRequiresPassword(
    tenantId: string,
    openDrawerRequiresPassword: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/open-drawer-requires-password`,
        { openDrawerRequiresPassword },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating drawer password requirement setting'));
    }
  }

  // Summary: Manager sets the password used for manual drawer open from POS hamburger.
  async updateOpenDrawerPassword(
    tenantId: string,
    openDrawerPassword: string,
    currentOpenDrawerPassword: string | undefined,
    forceResetOpenDrawerPassword: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      const payload = {
        openDrawerPassword,
        ...(currentOpenDrawerPassword ? { currentOpenDrawerPassword } : {}),
        ...(forceResetOpenDrawerPassword ? { forceResetOpenDrawerPassword: true } : {}),
      };
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/open-drawer-password`,
        payload,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating drawer password'));
    }
  }

  async updateZReportEmailRecipients(tenantId: string, payload: UpdateZReportEmailRecipientsRequest, employeeSession: string): Promise<void> {
    await authenticatedInstance.patch(`/sessions/tenant/${tenantId}/z-report-email-recipients`, payload,
      { headers: { 'x-employee-session': employeeSession } });
  }

  async sendZReportEmail(tenantId: string, sessionId: number, payload: SendZReportEmailRequest, employeeSession: string): Promise<void> {
    await authenticatedInstance.post(`/sessions/tenant/${tenantId}/z-report-email/${sessionId}`, payload,
      { headers: { 'x-employee-session': employeeSession } });
  }

  // Summary: Manager toggles whether non-manager employees can close day / Z-report from POS.
  async updateEmployeeShiftCloseEnabled(
    tenantId: string,
    employeeShiftCloseEnabled: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/employee-shift-close-enabled`,
        { employeeShiftCloseEnabled },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating employee day close setting'));
    }
  }

  // Summary: Manager updates tenant area print rules (persisted in pos_settings).
  async updateAreaPrintRules(
    tenantId: string,
    areaPrintRules: import('shared-models/browser').AreaPrintRule[],
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/area-print-rules`,
        { areaPrintRules },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating area print rules'));
    }
  }

  // Summary: Manager toggles whether non-managers can open a self attendance report from the POS hamburger.
  async updateEmployeeAttendanceReportEnabled(
    tenantId: string,
    employeeAttendanceReportEnabled: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/employee-attendance-report-enabled`,
        { employeeAttendanceReportEnabled },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating employee attendance report setting'));
    }
  }

  // Summary: Manager updates attendance overtime/special-day report rules for tenant-wide payroll report calculations.
  async updateAttendanceReportRules(
    tenantId: string,
    attendanceReportRules: AttendanceReportRules,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/sessions/tenant/${tenantId}/attendance-report-rules`,
        { attendanceReportRules },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating attendance report rules'));
    }
  }

  // Summary: Auto-closed sessions (last 24h) whose Z is unprinted — reconnect catch-up list.
  async listPendingAutoCloseZPrints(tenantId: string, employeeSession: string): Promise<number[]> {
    try {
      const response = await authenticatedInstance.get<number[]>(
        `/sessions/tenant/${tenantId}/pending-z-prints`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading pending Z-prints'));
    }
  }

  // Summary: Claims the single Z-print for an auto-closed session; true only for the winner.
  async claimAutoCloseZPrint(tenantId: string, sessionId: number, employeeSession: string): Promise<boolean> {
    try {
      const response = await authenticatedInstance.post<{ won: boolean }>(
        `/sessions/tenant/${tenantId}/${sessionId}/claim-z-print`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data.won;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed claiming Z-print'));
    }
  }

  // Summary: Closes the currently active tenant session from manager settings controls.
  // Details: docs/function-library/web/services.md#sessions-service
  async close(
    tenantId: string,
    employeeSession: string,
    payload?: {
      cashDrawerPrinterId?: number;
      closingCash?: number;
      nextOpeningCash?: number;
      zReportPrint?: ZReportPrintRequest;
    },
  ): Promise<SessionView> {
    try {
      const body = {
        ...(payload?.cashDrawerPrinterId != null ? { cashDrawerPrinterId: payload.cashDrawerPrinterId } : {}),
        ...(payload?.closingCash != null ? { closingCash: payload.closingCash } : {}),
        ...(payload?.nextOpeningCash != null ? { nextOpeningCash: payload.nextOpeningCash } : {}),
        ...(payload?.zReportPrint ? { zReportPrint: payload.zReportPrint } : {}),
      };
      const response = await authenticatedInstance.patch<SessionView>(
        `/sessions/tenant/${tenantId}/close`,
        body,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } }).response?.status;
      const message = extractServiceErrorMessage(error, '');
      if (status === 409 && /order\(s\) still open/i.test(message)) {
        const match = message.match(/(\d+)\s+order/i);
        throw new SessionHasOpenOrdersError(match ? Number(match[1]) : null);
      }
      throw new Error(message);
    }
  }

  async updateAttendanceUiEnabled(tenantId: string, attendanceUiEnabled: boolean, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch(
        `/sessions/tenant/${tenantId}/attendance-ui`,
        { attendanceUiEnabled },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating attendance UI setting'));
    }
  }

  async updateShiftCloseCashBalancesEnabled(
    tenantId: string,
    shiftCloseCashBalancesEnabled: boolean,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch(
        `/sessions/tenant/${tenantId}/shift-close-cash-balances`,
        { shiftCloseCashBalancesEnabled },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating shift-close cash setting'));
    }
  }
}

export const sessionsService = new SessionsService();





